/**
 * Join/Leave-Announcer-Settings pro Server (Slice 1): Toggles, Cooldown,
 * Template-Editor (3 Join-Zeilen + Leave), Variablen-Referenz, Live-Preview.
 * Preview rendert CLIENT-seitig über das geteilte reine Render-Modul (instant).
 * Der Worker nutzt diese Settings in Slice 1 NOCH NICHT.
 */

import { useSignal, useSignalEffect } from "@preact/signals";
import { useRef } from "preact/hooks";
import { api, activeServer, hasPermission } from "../store";
import { PageHead, Panel, NoServer } from "../components";
import { toast } from "../toast";
import type { ApiResponse } from "../api";
import { t } from "../i18n";
import {
  DEFAULT_JOIN_ANNOUNCER,
  ANNOUNCER_VARIABLES,
  SAMPLE_ANNOUNCER_VARS,
  renderJoinLines,
  renderLeaveLine,
  type JoinAnnouncerSettings,
} from "../../announcer/render";

type LoadState = "loading" | "ok" | "error";

export function JoinAnnouncer() {
  const s = useSignal<JoinAnnouncerSettings>({ ...DEFAULT_JOIN_ANNOUNCER });
  const loadState = useSignal<LoadState>("loading");
  const saving = useSignal(false);
  const loadGen = useRef(0);
  const canEdit = hasPermission("action.edit_announcer");

  async function load() {
    const sv = activeServer.value;
    if (!sv) return;
    const gen = ++loadGen.current;
    loadState.value = "loading";
    try {
      const r = await api.call<ApiResponse & { settings: JoinAnnouncerSettings }>("join_announcer_get", { server: sv });
      if (gen !== loadGen.current) return;
      if (r.ok && r.settings) { s.value = r.settings; loadState.value = "ok"; }
      else loadState.value = "error";
    } catch {
      if (gen === loadGen.current) loadState.value = "error";
    }
  }
  useSignalEffect(() => { if (activeServer.value) void load(); });

  async function save() {
    const sv = activeServer.value;
    if (!sv || !canEdit) return;
    saving.value = true;
    try {
      const r = await api.call<ApiResponse & { settings: JoinAnnouncerSettings }>("join_announcer_save", { server: sv, ...s.value });
      if (r.ok && r.settings) { s.value = r.settings; toast(t("joinAnnouncer.saved"), "ok"); }
      else toast(String(r.error ?? t("common.error")), "err");
    } finally {
      saving.value = false;
    }
  }

  const set = <K extends keyof JoinAnnouncerSettings>(k: K, v: JoinAnnouncerSettings[K]) => {
    s.value = { ...s.value, [k]: v };
  };
  const reset = () => { s.value = { ...DEFAULT_JOIN_ANNOUNCER }; };

  if (!activeServer.value) return (<><PageHead title={t("nav.joinAnnouncer")} /><NoServer /></>);

  const cur = s.value;
  const previewJoin = renderJoinLines(cur, SAMPLE_ANNOUNCER_VARS, { statsAvailable: cur.statsEnabled });
  const previewLeave = renderLeaveLine(cur, SAMPLE_ANNOUNCER_VARS);

  function Toggle({ label, k }: { label: string; k: "joinEnabled" | "leaveEnabled" | "statsEnabled" | "colorEnabled" }) {
    return (
      <label class="row" style="justify-content:space-between;gap:var(--s3)">
        <span>{label}</span>
        <input type="checkbox" checked={cur[k]} disabled={!canEdit} onChange={(e) => set(k, (e.target as HTMLInputElement).checked)} />
      </label>
    );
  }
  function TplField({ label, k }: { label: string; k: "joinLine1" | "joinLine2" | "joinLine3" | "leaveTemplate" }) {
    return (
      <div class="field">
        <label>{label}</label>
        <input class="input mono" value={cur[k]} disabled={!canEdit} onInput={(e) => set(k, (e.target as HTMLInputElement).value)} />
      </div>
    );
  }

  const body = (
    <div class="ja-grid">
      <Panel title={t("joinAnnouncer.settings")}>
        <div class="stack">
          <Toggle label={t("joinAnnouncer.joinEnabled")} k="joinEnabled" />
          <Toggle label={t("joinAnnouncer.leaveEnabled")} k="leaveEnabled" />
          <Toggle label={t("joinAnnouncer.statsEnabled")} k="statsEnabled" />
          <Toggle label={t("joinAnnouncer.colorEnabled")} k="colorEnabled" />
          <div class="field">
            <label>{t("joinAnnouncer.cooldown")}</label>
            <input class="input mono" type="number" min={0} max={3600} value={cur.cooldownSeconds} disabled={!canEdit}
              onInput={(e) => set("cooldownSeconds", Number((e.target as HTMLInputElement).value) || 0)} />
            <div class="caption">{t("joinAnnouncer.cooldownHint")}</div>
          </div>
        </div>
      </Panel>

      <Panel title={t("joinAnnouncer.templates")}>
        <div class="stack">
          <TplField label={t("joinAnnouncer.joinLine1")} k="joinLine1" />
          <TplField label={t("joinAnnouncer.joinLine2")} k="joinLine2" />
          <TplField label={t("joinAnnouncer.joinLine3")} k="joinLine3" />
          <TplField label={t("joinAnnouncer.leaveTemplate")} k="leaveTemplate" />
          <div class="caption">{t("joinAnnouncer.statsLineHint")}</div>
        </div>
      </Panel>

      <Panel title={t("joinAnnouncer.variables")}>
        <div class="ja-vars">
          {ANNOUNCER_VARIABLES.map((v) => (
            <span key={v.name} class="badge mono" title={`${t("joinAnnouncer.default")}: ${v.default}${v.optional ? " · optional" : ""}`}>
              {`{${v.name}}`}
            </span>
          ))}
        </div>
        <div class="caption">{t("joinAnnouncer.variablesHint")}</div>
      </Panel>

      <Panel title={t("joinAnnouncer.preview")}>
        <div class="ja-preview mono">
          {previewJoin.length === 0 && previewLeave === "" ? (
            <div class="faint">{t("joinAnnouncer.previewEmpty")}</div>
          ) : (
            <>
              <div class="caption">{t("joinAnnouncer.previewJoin")}</div>
              {previewJoin.map((l, i) => <div key={`j${i}`} class="ja-line">{l}</div>)}
              {previewJoin.length === 0 && <div class="faint">{t("joinAnnouncer.joinOffOrEmpty")}</div>}
              <div class="caption" style="margin-top:var(--s2)">{t("joinAnnouncer.previewLeave")}</div>
              {previewLeave !== "" ? <div class="ja-line">{previewLeave}</div> : <div class="faint">{t("joinAnnouncer.leaveOffOrEmpty")}</div>}
            </>
          )}
          <div class="caption" style="margin-top:var(--s2)">{t("joinAnnouncer.previewNote")}</div>
        </div>
      </Panel>
    </div>
  );

  return (
    <div class="view-enter">
      <PageHead title={t("nav.joinAnnouncer")} sub={t("joinAnnouncer.sub")}>
        {loadState.value === "ok" && (
          <>
            <button class="btn sm ghost" onClick={reset} disabled={!canEdit}>{t("joinAnnouncer.reset")}</button>
            <button class="btn primary" onClick={() => void save()} disabled={!canEdit || saving.value}>{t("common.save")}</button>
          </>
        )}
      </PageHead>

      {loadState.value === "loading" && <Panel title={t("nav.joinAnnouncer")}><div class="empty">{t("joinAnnouncer.loading")}</div></Panel>}
      {loadState.value === "error" && (
        <Panel title={t("nav.joinAnnouncer")}>
          <div class="empty stack" style="gap:var(--s3)">
            <div class="banner error">{t("joinAnnouncer.loadError")}</div>
            <button class="btn sm" onClick={() => void load()}>↻ {t("joinAnnouncer.retry")}</button>
          </div>
        </Panel>
      )}
      {loadState.value === "ok" && body}
      {!canEdit && loadState.value === "ok" && <div class="caption" style="margin-top:var(--s3)">{t("joinAnnouncer.readonly")}</div>}
    </div>
  );
}
